// Composição do prazo de entrega e retirada na vitrine.
//
// A API de cobertura devolve os tempos SEPARADOS e já elegeu a modalidade. Ela não soma
// porque a soma depende da origem do estoque, que só o consumidor conhece. Este módulo é o
// consumidor.
//
// ─────────────────────────────────────────────────────────────────────────────
// DUAS ARMADILHAS DE UNIDADE E DUPLA CONTAGEM. Leia antes de mexer.
//
// 1. `tempo_contingencia_gam` está em HORAS, não em minutos. A coluna é um INTEGER sem
//    unidade declarada no modelo, então a autoridade é o uso: `NewOrderRevision:194` faz
//    `maxWaitingDate.setHours(maxWaitingDate.getHours() + store.tempo_contingencia_gam)` e a
//    UI do checkout escreve "em {N}h" nas linhas 450 e 476.
//
// 2. `tempo_preparacao` NÃO entra na soma, apesar de vir no payload.
//    `OrderService.checkStoreIsOpenV2:55` (api) faz `addMinutes(referenceDate,
//    minutesPreparationStore)` ANTES de derivar `data_disponibilidade` e `hora_inicio`, e
//    `buildCoveragePayload` chama exatamente essa função passando os minutos. A preparação
//    já está dentro da janela de disponibilidade: somá-la de novo aqui contaria duas vezes e
//    faria a vitrine prometer prazo maior que o checkout.
// ─────────────────────────────────────────────────────────────────────────────
//
// A formatação é sempre `getReceiptTime`, a mesma do checkout. Prazo prometido na vitrine e
// exibido na finalização usam uma regra só, que é o ponto deste escopo.

import { getReceiptTime } from "./friendlyDate";
import { DeliveryType } from "./deliveryType";

export const StockOrigin = {
  LOJA: "LOJA", // estoque associado na própria farmácia
  GAM: "GAM", // vem do centro de distribuição
  NENHUM: "NENHUM"
};

// Linha de `produto_estoque` da farmácia, com o GAM derivado.
//
// O GAM sai de `quantidade - quantidade_associada`, e não de `quantidade_gam`, por dois
// motivos: é o que `NewOrderRevision:202` faz, e `GET /products`, `productsBanner` e
// `ProductRepository` não selecionam `quantidade_gam` — a subtração é a única forma que
// funciona em qualquer payload de produto.
export function getStockForStore(product, lojaId) {
  const stock = product?.estoques?.find((e) => e.loja_id === lojaId);

  if (!stock) return { associada: 0, gam: 0, origem: StockOrigin.NENHUM };

  const associada = stock.quantidade_associada || 0;
  const gam = Math.max((stock.quantidade || 0) - associada, 0);

  let origem = StockOrigin.NENHUM;
  if (associada > 0) origem = StockOrigin.LOJA;
  else if (gam > 0) origem = StockOrigin.GAM;

  return { associada, gam, origem };
}

// Janela de contingência, replicada de `NewOrderRevision.loadProductsFromStore:186-195`.
//
// Quando a farmácia reabre dentro da janela, o item conta como primeira entrega e a
// contingência não é somada. Sem esta regra a vitrine ficaria mais pessimista que o
// checkout para o mesmo item — exatamente a divergência que este escopo elimina.
export function canWaitStore(loja, agora = new Date()) {
  if (!loja || !loja.data_disponibilidade || !loja.hora_fim) return false;

  const horas = Number(loja.tempo_contingencia_gam);
  if (!Number.isFinite(horas)) return false;

  const fechamento = new Date(`${loja.data_disponibilidade} ${loja.hora_fim}`);
  if (Number.isNaN(fechamento.getTime())) return false;

  const limite = new Date(agora.getTime());
  limite.setHours(limite.getHours() + horas);

  return limite <= fechamento;
}

// Soma horas a um tempo "HH:MM:SS". Passar de 24h é intencional e legível: `formatTime`
// apenas lê os componentes, então "25:30:00" vira "25h e 30 min".
export function addHoursToTime(time, hours) {
  if (!time || !hours) return time;

  const [h, m, s] = String(time).split(":");
  const total = Number(h) + Number(hours);

  if (!Number.isFinite(total)) return time;

  return `${String(total).padStart(2, "0")}:${m || "00"}:${s || "00"}`;
}

// `operationTime` no formato que `getReceiptTime` espera. Cada modalidade carrega sua
// própria janela e seu respectivo tempo de entrega, sem parear campos achatados.
export function buildDeliveryOperationTime(
  modalidade,
  horasContingencia = 0
) {
  if (!modalidade) return null;

  const { horario } = modalidade;
  if (!horario || !horario.tempo_entrega) return null;

  return {
    data_disponibilidade: horario.data_disponibilidade,
    hora_inicio: horario.hora_inicio,
    hora_fim: horario.hora_fim,
    tempo_entrega: addHoursToTime(horario.tempo_entrega, horasContingencia)
  };
}

export function buildPickupOperationTime(loja, horasContingencia = 0) {
  if (!loja || !loja.tempo_para_retirada) return null;

  const horario = loja.lojaHorario || loja;

  return {
    data_disponibilidade: horario.data_disponibilidade,
    hora_inicio: horario.hora_inicio,
    hora_fim: horario.hora_fim,
    tempo_para_retirada: addHoursToTime(loja.tempo_para_retirada, horasContingencia)
  };
}

// `getReceiptTime` estoura em tempo nulo (`formatTime` faz `.split` do valor) e devolve
// `undefined` no ramo "loja fechada e sem data futura". As duas saídas viram null aqui.
function formatReceipt(operationTime, deliveryType) {
  if (!operationTime) return null;

  try {
    return getReceiptTime(operationTime, deliveryType, true) || null;
  } catch (_) {
    return null;
  }
}

export function getDeliveryCost(modalidade) {
  if (!modalidade) return null;

  // DECIMAL do driver pg chega como string.
  const bruto = modalidade.custo;
  const valor = parseFloat(bruto);
  return Number.isFinite(valor) ? valor : null;
}

// Quantas horas de contingência somar, dada a origem do estoque naquela farmácia.
function horasContingencia(loja, origem, agora) {
  if (origem !== StockOrigin.GAM) return 0;
  if (canWaitStore(loja, agora)) return 0;
  return Number(loja.tempo_contingencia_gam) || 0;
}

function lojaComHorario(loja, horario) {
  return horario ? { ...loja, ...horario } : loja;
}

// Compõe as duas linhas do card. Entrega e retirada podem ser farmácias diferentes, então a
// origem do estoque é avaliada em cada uma separadamente.
export function composeDeliveryEstimate({
  product,
  lojaEntrega,
  lojaRetirada,
  agora = new Date()
}) {
  const estoqueEntrega = lojaEntrega
    ? getStockForStore(product, lojaEntrega.loja_id)
    : { origem: StockOrigin.NENHUM };
  const estoqueRetirada = lojaRetirada
    ? getStockForStore(product, lojaRetirada.loja_id)
    : { origem: StockOrigin.NENHUM };

  let entregaExpressa = null;
  let entregaNormal = null;
  if (lojaEntrega && estoqueEntrega.origem !== StockOrigin.NENHUM) {
    const contingenciaNormal = horasContingencia(
      lojaComHorario(lojaEntrega, lojaEntrega.entregaNormal?.horario),
      estoqueEntrega.origem,
      agora
    );
    const contingenciaExpressa = horasContingencia(
      lojaComHorario(lojaEntrega, lojaEntrega.entregaExpressa?.horario),
      estoqueEntrega.origem,
      agora
    );

    const textoExpresso = formatReceipt(
      buildDeliveryOperationTime(lojaEntrega.entregaExpressa, contingenciaExpressa),
      DeliveryType.RECEBER_EM_CASA
    );

    if (textoExpresso) {
      entregaExpressa = {
        texto: textoExpresso,
        custo: getDeliveryCost(lojaEntrega.entregaExpressa),
        expressa: true
      };
    }

    const texto = formatReceipt(
      buildDeliveryOperationTime(lojaEntrega.entregaNormal, contingenciaNormal),
      DeliveryType.RECEBER_EM_CASA
    );

    if (texto) {
      entregaNormal = {
        texto,
        custo: getDeliveryCost(lojaEntrega.entregaNormal),
        expressa: false
      };
    }
  }

  let retirada = null;
  if (lojaRetirada && estoqueRetirada.origem !== StockOrigin.NENHUM) {
    const texto = formatReceipt(
      buildPickupOperationTime(
        lojaRetirada,
        horasContingencia(lojaRetirada, estoqueRetirada.origem, agora)
      ),
      DeliveryType.RETIRAR_NA_LOJA
    );

    if (texto) retirada = { texto };
  }

  return {
    entregaExpressa,
    entregaNormal,
    retirada,
    // A origem exibida no card é a da entrega, que é a linha principal; caindo para a
    // retirada quando só ela existe.
    origem:
      estoqueEntrega.origem !== StockOrigin.NENHUM
        ? estoqueEntrega.origem
        : estoqueRetirada.origem
  };
}
